import React, { useState, useEffect } from "react";
import { useLang } from "../lib/lang";
import { useSeo } from "../lib/seo";
import { api } from "../lib/api";

const init = {
  name: "", email: "", company: "", role: "", website: "",
  stage: "", team_size: "",
  why_brand_office: "", business_situation: "", scope_needed: "",
  budget_confirmed: false, start_date: "",
};

export default function ApplyPage() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(init);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  useSeo({
    title: lang === "es"
      ? "Aplicar a Brand Office — Brand and Behavior"
      : "Apply to Brand Office — Brand and Behavior",
    description: lang === "es"
      ? "Aplica al programa Brand Office. Asesoría estratégica de marca por aplicación, mínimo 3 meses."
      : "Apply to the Brand Office program. Application-based strategic brand advisory, minimum 3 months.",
    path: lang === "es" ? "/es/apply" : "/apply",
  });

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSubmitting(true);
    try {
      await api.post("/applications", form);
      setSent(true);
    } catch (e) {
      setErr(t("Could not submit. Please try again.", "No se pudo enviar. Inténtalo de nuevo."));
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <main className="app-main" data-testid="apply-success">
        <section className="page-hero">
          <div className="kicker">Brand Office</div>
          <h1><em className="it">{t("Application received.", "Aplicación recibida.")}</em></h1>
          <p>{t("We review applications weekly. You will hear from us at the email you provided.", "Revisamos aplicaciones semanalmente. Recibirás respuesta en el email que indicaste.")}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="app-main" data-testid="apply-page">
      <section className="page-hero">
        <div className="kicker">Brand Office</div>
        <h1>{t("Apply for ", "Aplica a ")}<em className="it">Brand Office</em>.</h1>
        <p>{t("Limited capacity. Minimum 3 months. Price provided after review.", "Capacidad limitada. Mínimo 3 meses. Precio entregado tras la revisión.")}</p>
      </section>

      <form className="form-card" onSubmit={submit} data-testid="apply-form">
        <div className="form-section">
          <div className="head"><h3>{t("About you", "Sobre ti")}</h3><div className="form-kicker">{t("Required", "Requerido")}</div></div>
          <div className="body">
            <div className="form-row"><label>{t("Full name", "Nombre completo")}</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="apply-name" /></div>
            <div className="form-row"><label>Email</label><input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" /></div>
            <div className="form-row"><label>{t("Company", "Compañía")}</label><input required value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="apply-company" /></div>
            <div className="form-row"><label>{t("Your role", "Tu rol")}</label><input value={form.role} onChange={(e) => set("role", e.target.value)} /></div>
            <div className="form-row"><label>{t("Website / LinkedIn", "Web / LinkedIn")}</label><input value={form.website} onChange={(e) => set("website", e.target.value)} /></div>
          </div>
        </div>

        <div className="form-section">
          <div className="head"><h3>{t("Business", "Negocio")}</h3><div className="form-kicker">{t("Context", "Contexto")}</div></div>
          <div className="body">
            <div className="form-row"><label>{t("Stage", "Etapa")}</label>
              <select value={form.stage} onChange={(e) => set("stage", e.target.value)}>
                <option value="">—</option>
                <option>Pre-launch</option>
                <option>Early stage</option>
                <option>Growth</option>
                <option>Scaling</option>
                <option>Established</option>
              </select>
            </div>
            <div className="form-row"><label>{t("Team size", "Tamaño del equipo")}</label><input value={form.team_size} onChange={(e) => set("team_size", e.target.value)} /></div>
            <div className="form-row"><label>{t("Current business situation", "Situación actual del negocio")}</label><textarea value={form.business_situation} onChange={(e) => set("business_situation", e.target.value)} /></div>
          </div>
        </div>

        <div className="form-section">
          <div className="head"><h3>{t("Fit", "Encaje")}</h3><div className="form-kicker">{t("Why Brand Office", "Por qué Brand Office")}</div></div>
          <div className="body">
            <div className="form-row"><label>{t("Why Brand Office (not Brand Partner)?", "¿Por qué Brand Office (y no Brand Partner)?")}</label><textarea required value={form.why_brand_office} onChange={(e) => set("why_brand_office", e.target.value)} data-testid="apply-why" /></div>
            <div className="form-row"><label>{t("Scope you need help with", "Alcance en el que necesitas ayuda")}</label><textarea value={form.scope_needed} onChange={(e) => set("scope_needed", e.target.value)} /></div>
            <div className="form-row"><label>{t("Preferred start date", "Fecha preferida de inicio")}</label><input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} /></div>
            <div className="form-row">
              <label style={{ display: "flex", alignItems: "center", gap: 10, textTransform: "none", letterSpacing: 0, fontSize: 14, color: "var(--ink)" }}>
                <input type="checkbox" checked={form.budget_confirmed} onChange={(e) => set("budget_confirmed", e.target.checked)} />
                {t("I understand Brand Office is a high-touch engagement and budget will be discussed after review.", "Entiendo que Brand Office es un engagement de alta involucración y que el presupuesto se discute tras la revisión.")}
              </label>
            </div>
          </div>
        </div>

        {err && <div className="banner-warning">{err}</div>}
        <div className="form-actions">
          <button type="submit" disabled={submitting} className="button" data-testid="apply-submit">
            {submitting ? t("Submitting…", "Enviando…") : t("Submit application", "Enviar aplicación")}
          </button>
        </div>
      </form>
    </main>
  );
}
